import { AccessField } from './field'
import { AccessObservations } from './observations'
import { AccessButtons } from './buttons'
import { GunControls } from './gun-controls'
import { useWindowFocus } from '../use-window-focus'
import PropTypes from 'prop-types'

const EnterActionSwitch = ({ enterEvent, setEnterEvent }) => {
  const isEntry = enterEvent === 'entry'
  const nextEvent = isEntry ? 'exit' : 'entry'

  return (
    <div className={`rounded-lg border p-3 ${isEntry ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${isEntry ? 'text-green-700' : 'text-red-700'}`}>
            Enter registrará {isEntry ? 'entrada' : 'salida'}
          </p>
          <p className={`text-xs ${isEntry ? 'text-green-700' : 'text-red-700'}`}>
            Al presionar Enter se usará la acción {isEntry ? 'Registrar Entrada' : 'Registrar Salida'}.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!isEntry}
          aria-label={`Cambiar Enter a ${nextEvent === 'entry' ? 'entrada' : 'salida'}`}
          onClick={() => setEnterEvent(nextEvent)}
          className={[
            'relative inline-flex h-8 w-16 shrink-0 items-center rounded-full border transition',
            isEntry ? 'bg-green-500 border-green-600' : 'bg-red-500 border-red-600'
          ].join(' ')}
        >
          <span
            className={[
              'absolute h-6 w-6 rounded-full bg-white shadow transition',
              isEntry ? 'left-1' : 'left-9'
            ].join(' ')}
          />
          <span className="sr-only">
            {isEntry ? 'Entrada' : 'Salida'}
          </span>
        </button>
      </div>
    </div>
  )
}

EnterActionSwitch.propTypes = {
  enterEvent: PropTypes.oneOf(['entry', 'exit']).isRequired,
  setEnterEvent: PropTypes.func.isRequired
}

export const AccessForm = ({
  documento,
  handleChange,
  handleBlur,
  showObservations,
  setShowObservations,
  observations,
  setObservations,
  handleClear,
  handleSubmit,
  loading,
  officeId,
  isOfficePreselected,
  gunMode,
  setGunMode,
  gunDefaultEvent,
  setGunDefaultEvent,
  gunAutoSubmit,
  setGunAutoSubmit,
  canAutoSubmitNow,
  enterEvent,
  setEnterEvent
}) => {
  const windowActive = useWindowFocus()
  const scanArmed = gunMode && windowActive

  return (
    <form
      onSubmit={(e) => handleSubmit(e, enterEvent)}
      onKeyDown={(e) => {
        if (gunMode && e.target?.id === 'documento-input' && e.key !== 'Tab') {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      className="space-y-4"
    >
      <GunControls
        gunMode={gunMode}
        setGunMode={setGunMode}
        gunDefaultEvent={gunDefaultEvent}
        setGunDefaultEvent={setGunDefaultEvent}
        gunAutoSubmit={gunAutoSubmit}
        setGunAutoSubmit={setGunAutoSubmit}
        canAutoSubmitNow={canAutoSubmitNow}
      />

      <AccessField
        documento={documento}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleClear={handleClear}
        gunMode={gunMode}
        scanArmed={scanArmed}
      />

      <AccessObservations
        showObservations={showObservations}
        setShowObservations={setShowObservations}
        observations={observations}
        setObservations={setObservations}
        locked={gunAutoSubmit}
      />

      <AccessButtons
        loading={loading}
        isOfficePreselected={isOfficePreselected}
        officeId={officeId}
        handleSubmit={handleSubmit}
      />

      <EnterActionSwitch
        enterEvent={enterEvent}
        setEnterEvent={setEnterEvent}
      />
    </form>
  )
}

AccessForm.propTypes = {
  documento: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  showObservations: PropTypes.bool.isRequired,
  setShowObservations: PropTypes.func.isRequired,
  observations: PropTypes.string.isRequired,
  setObservations: PropTypes.func.isRequired,
  handleClear: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  officeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOfficePreselected: PropTypes.bool.isRequired,
  gunMode: PropTypes.bool.isRequired,
  setGunMode: PropTypes.func.isRequired,
  gunDefaultEvent: PropTypes.string.isRequired,
  setGunDefaultEvent: PropTypes.func.isRequired,
  gunAutoSubmit: PropTypes.bool.isRequired,
  setGunAutoSubmit: PropTypes.func.isRequired,
  canAutoSubmitNow: PropTypes.bool.isRequired,
  enterEvent: PropTypes.oneOf(['entry', 'exit']).isRequired,
  setEnterEvent: PropTypes.func.isRequired
}
